import React, { useEffect, useState } from 'react';
import { ArrowPathIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { reportsAPI } from '../../api/client';

const STATUS_LABELS = {
  pending: 'Pending screening', screening: 'Screening',
  screened_pending_review: 'Awaiting HR review', unreadable_cv: 'Unreadable CV',
  passed: 'Passed', failed: 'Failed', interview_invited: 'Interview invited',
  details_requested: 'Details requested', rejected: 'Rejected', no_response: 'No response',
  form_sent: 'Waiting for form', form_submitted: 'Form submitted',
  schedule_sent: 'Waiting for booking', interview_scheduled: 'Interview scheduled',
  interviewed: 'Interviewed',
};

function localDay(day = new Date()) {
  return `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
}

function Metric({ label, value, note }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <dt className="text-sm text-gray-600">{label}</dt>
      <dd className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">{value ?? 'N/A'}</dd>
      {note && <p className="mt-1 text-xs leading-5 text-gray-500">{note}</p>}
    </div>
  );
}

export default function PositionSummary({ deptId, positions, canView }) {
  const [positionId, setPositionId] = useState('');
  const [dateFrom, setDateFrom] = useState(() => {
    const day = new Date();
    day.setDate(day.getDate() - 6);
    return localDay(day);
  });
  const [dateTo, setDateTo] = useState(() => localDay());
  const [refresh, setRefresh] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const validDates = Boolean(dateFrom && dateTo && dateFrom <= dateTo);

  useEffect(() => {
    if (!canView || !validDates) return;
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    reportsAPI.positionSummary(deptId, { positionId, dateFrom, dateTo })
      .then(response => { if (active) setData(response.data); })
      .catch(err => {
        if (!active) return;
        setError(err.response?.status === 403
          ? 'You need HR Reports permission and access to this department.'
          : 'Could not load the position summary. Please try again.');
      })
      .finally(() => { if (active) setLoading(false); });
    // Rapid filter changes must not display an older request under new filters.
    return () => { active = false; };
  }, [deptId, positionId, dateFrom, dateTo, refresh, canView, validDates]);

  if (!canView) return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
      Position Summary needs HR Reports permission. Ask an admin to grant access.
    </div>
  );

  const current = data?.current;
  const activity = data?.activity;
  const hasHistory = activity?.coverage !== 'none';
  const selectedPosition = positions.find(p => String(p.id) === positionId)?.title || 'All positions';
  const inputClass = 'mt-1 min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-600';

  return (
    <section className="space-y-5" aria-label="Position Summary">
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <ChartBarIcon className="h-5 w-5 text-green-700" aria-hidden="true" />
              Position Summary
            </h2>
            <p className="mt-1 text-sm text-gray-600">Current progress, recorded calls and what needs attention.</p>
          </div>
          <button type="button" onClick={() => setRefresh(value => value + 1)} disabled={loading || !validDates}
            className="flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600 disabled:opacity-50">
            <ArrowPathIcon className="h-4 w-4" aria-hidden="true" /> Refresh
          </button>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <label htmlFor="summary-position" className="text-sm font-medium text-gray-700">Position
            <select id="summary-position" className={inputClass} value={positionId} onChange={e => setPositionId(e.target.value)}>
              <option value="">All positions</option>
              {positions.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
          <label htmlFor="summary-from" className="text-sm font-medium text-gray-700">Calls from
            <input id="summary-from" type="date" className={inputClass} value={dateFrom}
              max={dateTo || undefined} onChange={e => setDateFrom(e.target.value)} />
          </label>
          <label htmlFor="summary-to" className="text-sm font-medium text-gray-700">Calls to
            <input id="summary-to" type="date" className={inputClass} value={dateTo}
              min={dateFrom || undefined} onChange={e => setDateTo(e.target.value)} />
          </label>
        </div>
        <p className="mt-3 text-xs leading-5 text-gray-500">
          Dates filter call activity only. Pipeline and attention counts always show the current state across all intake dates.
        </p>
      </div>

      {!validDates ? (
        <p role="alert" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-900">Choose both dates with From on or before To.</p>
      ) : error ? (
        <p role="alert" className="rounded-xl bg-red-50 p-4 text-sm text-red-800">{error}</p>
      ) : loading ? (
        <p role="status" className="py-12 text-center text-sm text-gray-600">Loading position summary…</p>
      ) : data && (
        <>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{selectedPosition}</h3>
            <p className="text-xs text-gray-500">Updated {new Date(data.generated_at).toLocaleString(undefined, { timeZone: data.timezone })} · {data.timezone}</p>
          </div>

          <section aria-labelledby="summary-pipeline-heading">
            <h3 id="summary-pipeline-heading" className="mb-3 font-semibold text-gray-800">Current pipeline</h3>
            {current.total_candidates === 0 && <p className="mb-3 text-sm text-gray-600">No candidates in this selection yet.</p>}
            <dl className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              <Metric label="Total candidates" value={current.total_candidates} note="All intake dates" />
              <Metric label="Screening pending" value={current.screening_pending} note="Queued or currently screening" />
              <Metric label="Awaiting HR review" value={current.hr_review_pending} note="CV screening decision pending" />
              <Metric label="Waiting for form" value={current.form_waiting} note="Current status: form sent" />
              <Metric label="Waiting for booking" value={current.schedule_waiting} note="Current status: schedule sent" />
              <Metric label="Candidates with bookings" value={current.interviews_booked} note="Non-cancelled bookings, including past slots" />
            </dl>
            <details className="mt-3 rounded-xl border border-gray-200 bg-white p-4">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600">All candidate statuses</summary>
              <dl className="mt-3 grid gap-x-8 sm:grid-cols-2">
                {Object.entries(data.status_counts).map(([status, count]) => (
                  <div key={status} className="flex justify-between gap-3 border-t border-gray-100 py-2 text-sm">
                    <dt className="text-gray-600">{STATUS_LABELS[status] || status.replace(/_/g, ' ')}</dt>
                    <dd className="font-medium tabular-nums text-gray-900">{count}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-2 text-xs text-gray-500">This breakdown totals all candidates. The overview cards highlight selected stages.</p>
            </details>
          </section>

          <section aria-labelledby="summary-attention-heading" className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <h3 id="summary-attention-heading" className="font-semibold text-amber-950">Needs attention now</h3>
            <dl className="mt-3 grid gap-3 sm:grid-cols-2">
              <Metric label="Due now" value={data.actions.followups_due} note="Candidates eligible in the Follow-ups queue" />
              <Metric label="Due · waiting 7+ days" value={data.actions.waiting_7d_plus} note="Subset of Due now; measured from the unanswered invite" />
            </dl>
            <p className="mt-3 text-xs leading-5 text-amber-900">Use CV Screening → Follow-ups to contact these candidates. Snoozed, closed and already-booked candidates follow the existing queue rules.</p>
          </section>

          <section aria-labelledby="summary-activity-heading">
            <h3 id="summary-activity-heading" className="font-semibold text-gray-800">Recorded call activity</h3>
            <p className="mt-1 mb-3 text-xs leading-5 text-gray-500">{dateFrom} – {dateTo} · {data.timezone}. Repeat calls count separately; candidates contacted are counted once.</p>
            {(activity.coverage_note || !hasHistory || activity.calls_made === 0) && (
              <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                {activity.coverage_note || activity.empty_text}
              </p>
            )}
            <dl className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Metric label="Calls recorded" value={hasHistory ? activity.calls_made : null} />
              <Metric label="Candidates contacted" value={hasHistory ? activity.candidates_contacted : null} />
              <Metric label="Will respond" value={hasHistory ? activity.outcomes.called_will_respond : null} />
              <Metric label="No answer" value={hasHistory ? activity.outcomes.no_answer : null} />
              <Metric label="Closed" value={hasHistory ? activity.outcomes.closed : null} />
            </dl>
            <p className="mt-3 text-xs text-gray-500">Open Follow-up Activity for individual calls, notes and PDF export.</p>
          </section>
        </>
      )}
    </section>
  );
}
